import { ENGINE_URL } from "./service.config.js";

/**
 * TerminalStreamService
 * SSE (Server-Sent Events) Stream client bridge for Go Trading Engine.
 * Features:
 * - Two-phase state hydration support
 * - Event multiplexing: snapshot, delta, order_update, heartbeat
 * - Exponential backoff auto-reconnection
 * - Token & environment query serialization
 */
export class TerminalStreamService {
  constructor() {
    this.eventSource = null;
    this.listeners = new Map(); // event -> Set of callbacks
    this.reconnectAttempts = 0;
    this.reconnectTimeout = null;
    this.status = "DISCONNECTED"; // DISCONNECTED | CONNECTING | CONNECTED | RECONNECTING
    this.statusListeners = new Set();
    this.currentOptions = null;
    this.maxReconnectDelay = 30000;
    this.baseReconnectDelay = 1000;
  }

  /**
   * Subscribe to stream events (snapshot, delta, order_update, heartbeat, error)
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    return () => this.off(event, callback);
  }

  /**
   * Unsubscribe from stream events
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  /**
   * Subscribe to connection status changes
   */
  onStatusChange(callback) {
    this.statusListeners.add(callback);
    try {
      callback(this.status);
    } catch (err) {
      console.error("Error in status listener:", err);
    }
    return () => this.statusListeners.delete(callback);
  }

  emitStatus(newStatus) {
    this.status = newStatus;
    this.statusListeners.forEach((cb) => {
      try {
        cb(newStatus);
      } catch (err) {
        console.error("Error in status listener:", err);
      }
    });
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach((cb) => {
        try {
          cb(data);
        } catch (err) {
          console.error(`Error in ${event} listener:`, err);
        }
      });
    }
  }

  /**
   * Connect to SSE stream
   * @param {Object} options - { env: 'LIVE'|'SIMULATION', token?: string, ucc?: string, engineUrl?: string }
   * @param {boolean} [isReconnect=false]
   */
  connect(options = {}, isReconnect = false) {
    if (!isReconnect) {
      this.disconnect();
    } else {
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
      if (this.eventSource) {
        try {
          this.eventSource.close();
        } catch (e) {}
        this.eventSource = null;
      }
    }
    this.currentOptions = options;
    this.emitStatus("CONNECTING");

    const baseUrl = options.engineUrl || ENGINE_URL;
    const env = options.env || localStorage.getItem("dealer_terminal_env") || "LIVE";
    const token = options.token || localStorage.getItem("token") || "";
    const ucc = options.ucc || "";

    const params = new URLSearchParams();
    if (env) params.append("env", env);
    if (token) params.append("token", token);
    if (ucc) params.append("ucc", ucc);

    const streamUrl = `${baseUrl}/stream?${params.toString()}`;

    try {
      this.eventSource = new EventSource(streamUrl);

      this.eventSource.onopen = () => {
        this.reconnectAttempts = 0;
        this.emitStatus("CONNECTED");
      };

      this.eventSource.onerror = (err) => {
        console.warn("TerminalStreamService SSE connection error / dropped:", err);
        this.emit("stream_error", err);
        this.handleDisconnectAndReconnect();
      };

      // Built-in SSE Named Events
      const eventNames = [
        "hydration",
        "snapshot",
        "delta",
        "order_update",
        "heartbeat",
        "position_update",
        "fill",
        "cancel",
      ];
      eventNames.forEach((eventName) => {
        this.eventSource.addEventListener(eventName, (e) => {
          try {
            const parsed = JSON.parse(e.data);
            this.emit(eventName, parsed);
          } catch (jsonErr) {
            this.emit(eventName, e.data);
          }
        });
      });

      // Default message event fallback
      this.eventSource.onmessage = (e) => {
        try {
          const parsed = JSON.parse(e.data);
          const type = parsed.type || parsed.event || "message";
          this.emit(type, parsed.data || parsed);
        } catch (jsonErr) {
          this.emit("message", e.data);
        }
      };
    } catch (err) {
      console.error("Failed to initialize EventSource:", err);
      this.handleDisconnectAndReconnect();
    }
  }

  handleDisconnectAndReconnect() {
    if (this.eventSource) {
      try {
        this.eventSource.close();
      } catch (e) {}
      this.eventSource = null;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.emitStatus("RECONNECTING");

    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    );
    this.reconnectAttempts++;

    this.reconnectTimeout = setTimeout(() => {
      if (this.currentOptions) {
        this.connect(this.currentOptions, true);
      }
    }, delay);
  }

  /**
   * Close the active stream connection
   */
  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.eventSource) {
      try {
        this.eventSource.close();
      } catch (e) {}
      this.eventSource = null;
    }
    this.reconnectAttempts = 0;
    this.emitStatus("DISCONNECTED");
  }

  /**
   * Get current connection status
   */
  getStatus() {
    return this.status;
  }
}

export const terminalStream = new TerminalStreamService();
export default terminalStream;
